import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Checkbox,
  CircularProgress,
  Chip,
  InputAdornment,
  Pagination
} from '@mui/material';
import { Close, Add, Delete, List, Search } from '@mui/icons-material';
import { fetchData, postData } from '../../../apiService';
import SearchableDropdown from '../../common/SearchableDropdown';

const PurchaseReturnModal = ({ open, onClose, onSave, companyId, branchId, token, suppliers = [], setLoading, setLoadingMessage, setError, setSuccess, fetchPurchaseReturns, setCreateModalOpen, userId }) => {
  console.log('PurchaseReturnModal props:', { open, companyId, branchId, token: token ? 'exists' : 'missing', suppliersCount: suppliers.length });

  const [formData, setFormData] = useState({
    billNo: '',
    billDate: new Date().toISOString().split('T')[0],
    purchaseId: '',
    purchaseNo: '',
    supplierId: '',
    supplier: '',
    gstNo: '',
    invType: '',
    gstType: 'Excluding Tax'
  });

  const [itemsList, setItemsList] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [items, setItems] = useState([]);
  const [viewAllItemsModalOpen, setViewAllItemsModalOpen] = useState(false);
  
  // Get Items Modal State
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [getItemsLoading, setGetItemsLoading] = useState(false);
  const [addingItemsLoading, setAddingItemsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
  const [itemsPerPage] = useState(50);
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  
  const [validationError, setValidationError] = useState('');
  const [additionalCharges, setAdditionalCharges] = useState({
    freight: 0,
    packing: 0,
    tcsAmount: 0,
    paidAmount: 0,
    remarks: ''
  });

  const [summary, setSummary] = useState({
    subtotal: 0,
    discount: 0,
    taxable: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    freightGST: 0,
    packingGST: 0,
    effectiveGSTRate: 18,
    tcs: 0,
    roundOff: 0,
    total: 0,
    paid: 0
  });

  // Reset all state when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        billNo: '',
        billDate: new Date().toISOString().split('T')[0],
        purchaseId: '',
        purchaseNo: '',
        supplierId: '',
        supplier: '',
        gstNo: '',
        invType: '',
        gstType: 'Excluding Tax'
      });
      setItems([]);
      setAdditionalCharges({ freight: 0, packing: 0, tcsAmount: 0, paidAmount: 0, remarks: '' });
      setSummary({ subtotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, freightGST: 0, packingGST: 0, effectiveGSTRate: 18, tcs: 0, roundOff: 0, total: 0, paid: 0 });
      setValidationError('');
      setSelectedItems([]);
      setGetItemsSearchValue('');
    }
  }, [open]);

  // Check if invoice type is IGST
  // B to B and B to C use CGST+SGST, others use IGST
  const isIGSTInvoice = () => {
    const invoiceType = (formData.invType || '').toLowerCase();
    // If invoice type contains 'b to b' or 'b to c' or 'b2b' or 'b2c', use CGST+SGST
    if (invoiceType.includes('b to b') || invoiceType.includes('b to c') ||
      invoiceType.includes('b2b') || invoiceType.includes('b2c')) {
      return false; // Use CGST + SGST
    }
    // For IGST type or any other type, use IGST
    return invoiceType.includes('igst') || invoiceType.length > 0;
  };

  // Fetch items list
  useEffect(() => {
    const fetchItems = async () => {
      if (!companyId || !token || !open) return;

      try {
        const response = await fetchData('ItemApi', `/${companyId}`, token);
        const itemsData = response.response_data || response || [];
        console.log('Items fetched:', itemsData);
        setItemsList(Array.isArray(itemsData) ? itemsData : []);
      } catch (error) {
        console.error('Error fetching items:', error);
        setItemsList([]);
      }
    };

    fetchItems();
  }, [companyId, token, open]);

  // Fetch purchases list
  useEffect(() => {
    const fetchPurchases = async () => {
      if (!companyId || !branchId || !token || !open) return;

      try {
        const response = await fetchData('PurchaseApi', `/${companyId}/${branchId}`, token);
        const purchasesData = response.response_data || response || [];
        console.log('Purchases fetched:', purchasesData);
        setPurchases(Array.isArray(purchasesData) ? purchasesData : []);
      } catch (error) {
        console.error('Error fetching purchases:', error);
        setPurchases([]);
      }
    };

    fetchPurchases();
  }, [companyId, branchId, token, open]);

  // Fetch MaxBillNo
  useEffect(() => {
    const fetchMaxBillNo = async () => {
      if (!companyId || !branchId || !token || !open) return;

      try {
        const response = await fetchData('PurchaseReturnApi', `/MaxBillno/${companyId}/${branchId}`, token);
        const data = response.response_data || response || {};
        const billNumber = data.BillNumber || data.billNumber || 1;
        const billPrefix = data.BillPrefix || data.billPrefix || '';
        const finYear = data.FinYear || data.finYear || '';

        // Format: prefix/billno/finyear
        const fullBillNo = `${billPrefix}/${billNumber}/${finYear}`;
        setFormData(prev => ({ ...prev, billNo: fullBillNo }));
      } catch (error) {
        console.error('Error fetching MaxBillNo:', error);
        setFormData(prev => ({ ...prev, billNo: '1' }));
      }
    };

    fetchMaxBillNo();
  }, [companyId, branchId, token, open]);

  // Handle supplier selection - auto-fill InvoiceTypeName and GSTNumber
  const handleSupplierChange = (supplierId) => {
    console.log('Supplier selected:', supplierId);

    if (!supplierId) {
      setFormData(prev => ({
        ...prev,
        supplierId: '',
        supplier: '',
        gstNo: '',
        invType: '',
        purchaseId: '', // Clear purchase order selection
        purchaseNo: ''  // Clear purchase number
      }));
      // Clear items when supplier is cleared
      setItems([]);
      calculateSummary([], additionalCharges);
      return;
    }

    const selectedSupplier = suppliers.find(s => String(s.MasterId || s.Id) === String(supplierId));

    if (selectedSupplier) {
      setFormData(prev => ({
        ...prev,
        supplierId: supplierId,
        supplier: selectedSupplier.Name || '',
        gstNo: selectedSupplier.GSTNumber || selectedSupplier.GSTNo || '',
        invType: selectedSupplier.InvoiceTypeName || selectedSupplier.InvoiceType || '',
        purchaseId: '', // Clear purchase order selection when supplier changes
        purchaseNo: ''  // Clear purchase number when supplier changes
      }));
      
      // Clear items when supplier changes
      setItems([]);
      calculateSummary([], additionalCharges);
    }
  };

  // Recalculate when InvoiceType or GSTType changes
  useEffect(() => {
    if (items.length > 0 && (formData.invType || formData.gstType)) {
      const useIGST = isIGSTInvoice();

      const updatedItems = items.map(item => {
        const qty = Math.max(0, parseFloat(item.rcvdQty) || 0);
        const rate = Math.max(0, parseFloat(item.rate) || 0);
        const discPercent = Math.max(0, parseFloat(item.discPercent) || 0);
        const gstRate = Math.max(0, parseFloat(item.gstPercent) || 0);
        const isIncludingTax = formData.gstType === 'Including Tax';

        let taxableAmount, gstAmount, discountAmount;

        if (isIncludingTax) {
          const totalWithGst = qty * rate;
          const baseAmount = totalWithGst / (1 + gstRate / 100);
          discountAmount = (baseAmount * discPercent) / 100;
          taxableAmount = baseAmount - discountAmount;
          gstAmount = (taxableAmount * gstRate) / 100;
        } else {
          const baseAmount = qty * rate;
          discountAmount = (baseAmount * discPercent) / 100;
          taxableAmount = baseAmount - discountAmount;
          gstAmount = (taxableAmount * gstRate) / 100;
        }

        return {
          ...item,
          discAmt: Math.round(discountAmount * 100) / 100,
          taxable: Math.round(taxableAmount * 100) / 100,
          cgst: useIGST ? 0 : Math.round((gstAmount / 2) * 100) / 100,
          sgst: useIGST ? 0 : Math.round((gstAmount / 2) * 100) / 100,
          igst: useIGST ? Math.round(gstAmount * 100) / 100 : 0,
          total: Math.round((taxableAmount + gstAmount) * 100) / 100
        };
      });

      setItems(updatedItems);
      calculateSummary(updatedItems, additionalCharges);
    }
  }, [formData.invType, formData.gstType]);

  const handleChange = (field, value) => {
    console.log('handleChange called:', field, value);
    if (field === 'supplierId') {
      handleSupplierChange(value);
    } else if (field === 'purchaseId') {
      handlePurchaseChange(value);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Fetch single Purchase details
  const fetchSinglePurchase = async (purchaseId) => {
    try {
      const response = await fetchData('PurchaseReturnApi', `/PurchaseData/${companyId}/${purchaseId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching single purchase:', error);
      return null;
    }
  };

  // Handle purchase selection - auto-fill supplier and other details
  const handlePurchaseChange = async (purchaseId) => {
    console.log('Purchase selected:', purchaseId);

    if (!purchaseId) {
      // Reset purchase-related fields
      setFormData(prev => ({
        ...prev,
        purchaseId: '',
        purchaseNo: ''
      }));
      setItems([]); // Clear items
      return;
    }

    try {
      // Fetch full purchase details using the API
      const purchaseDetails = await fetchSinglePurchase(purchaseId);

      if (purchaseDetails) {
        // PurchaseReturnApi/PurchaseData wraps the header as PurchaseReturn (or Purchase)
        // and the line items as PurchaseReturnDetails / PurchaseDetails
        const purchaseData = purchaseDetails.PurchaseReturn || purchaseDetails.Purchase || purchaseDetails;
        const details = purchaseDetails.PurchaseReturnDetails || purchaseDetails.PurchaseDetails || [];

        console.log('Fetched purchase data:', purchaseData);
        console.log('Fetched purchase details:', details);

        // Auto-fill supplier and other details from purchase
        setFormData(prev => ({
          ...prev,
          purchaseId: purchaseId,
          purchaseNo: purchaseData.BillNumber || purchaseData.PurchaseNo || '',
          supplierId: String(purchaseData.MasterId || ''),
          supplier: purchaseData.MasterName || '',
          gstNo: purchaseData.GSTNumber || '',
          invType: purchaseData.InvoiceTypeName || '',
          gstType: purchaseData.GstCalculationType === 2 ? 'Including Tax' : 'Excluding Tax'
        }));

        // Populate items from purchase details
        const purchaseItems = details.map(detail => ({
          id: Date.now() + Math.random(), // Unique ID
          itemId: detail.ItemId || '',
          item: detail.ItemName || detail.Name || '',
          hsnCode: detail.HSNCode || detail.HSN || '',
          // Previous stock logic (same as PurchaseIndex "Purchased" column):
          // prefer PreviousStockQuantity, fall back to PurchaseQuantity.
          previousStockQty: detail.PreviousStockQuantity || 0,
          purchaseQty: detail.PurchaseQuantity || detail.ReceivedQuantity || detail.Quantity || 0,
          purchasedQty: (detail.PreviousStockQuantity > 0)
            ? detail.PreviousStockQuantity
            : (detail.PurchaseQuantity || detail.ReceivedQuantity || detail.Quantity || 0),
          rcvdQty: 0, // Return quantity starts at 0
          rate: detail.Rate || 0,
          discPercent: detail.DiscountPercentage || 0,
          discAmt: 0,
          gstPercent: detail.GSTRate || 0,
          taxable: 0,
          cgst: 0,
          sgst: 0,
          igst: 0,
          total: 0
        }));

        setItems(purchaseItems);
        calculateSummary(purchaseItems, additionalCharges);
      }
    } catch (error) {
      console.error('Error handling purchase selection:', error);
    }
  };

  const handleAdditionalChargeChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setAdditionalCharges(prev => {
      const updated = { ...prev, [field]: numValue };
      calculateSummary(items, updated);
      return updated;
    });
  };

  const handleAddItem = () => {
    // Validation: Check if the last row has data before adding a new row
    if (items.length > 0) {
      const lastItem = items[items.length - 1];

      // Check if the last item has an item selected and return quantity > 0
      if (!lastItem.itemId || lastItem.itemId === '') {
        setValidationError('Please select an item in the current row before adding a new one.');
        return;
      }

      if (!lastItem.rcvdQty || parseFloat(lastItem.rcvdQty) <= 0) {
        setValidationError('Please enter Return Qty in the current row before adding a new one.');
        return;
      }
    }

    // Clear validation error if validation passes
    setValidationError('');

    setItems(prev => [...prev, {
      id: Date.now(),
      itemId: '',
      item: '',
      hsnCode: '',
      purchasedQty: 0,
      rcvdQty: 0,
      rate: 0,
      discPercent: 0,
      discAmt: 0,
      gstPercent: 0,
      taxable: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      total: 0
    }]);
  };

  const handleDeleteItem = (id) => {
    const updatedItems = items.filter(item => item.id !== id);
    setItems(updatedItems);
    calculateSummary(updatedItems, additionalCharges);
  };

  // Get Items Modal Functions
  const handleOpenGetItemsModal = async () => {
    setGetItemsModalOpen(true);
    setGetItemsLoading(true);
    setSelectedItems([]);
    setGetItemsSearchValue('');
    setCurrentItemsPage(1);

    // Simulate loading - in real app, you would fetch items from API
    setTimeout(() => {
      setGetItemsLoading(false);
    }, 1000);
  };

  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearchValue('');
  };

  // Handle item selection in Get Items modal
  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      const newSelectedItem = {
        ...item,
        selectedQuantity: 1,
        selectedRate: item.PurchaseRate || item.SaleRate || 0,
        selectedDiscountPercentage: 0,
        selectedDiscountAmount: 0
      };
      setSelectedItems(prev => [...prev, newSelectedItem]);
    } else {
      setSelectedItems(prev => prev.filter(selectedItem => selectedItem.Id !== item.Id));
    }
  };

  // Update selected item details with bidirectional discount sync
  const handleSelectedItemChange = (itemId, field, value) => {
    // Validate discount percentage not above 100%
    if (field === 'selectedDiscountPercentage') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > 100) return;
    }

    // Validate discount amount doesn't exceed maximum allowed (100% of item amount)
    if (field === 'selectedDiscountAmount') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const selectedItem = selectedItems.find(item => item.Id === itemId);
        if (selectedItem) {
          const quantity = Math.max(0, parseFloat(selectedItem.selectedQuantity) || 0);
          const rate = Math.max(0, parseFloat(selectedItem.selectedRate) || 0);
          const amount = quantity * rate;
          const maxDiscountAmount = amount; // 100% of amount
          if (numValue > maxDiscountAmount) {
            // Block the input if it exceeds 100% of item amount
            return;
          }
        }
      }
    }

    setSelectedItems(prev => prev.map(item => {
      if (item.Id === itemId) {
        const updatedItem = { ...item, [field]: value };
        
        // Calculate bidirectional sync for discount fields
        if (field === 'selectedDiscountPercentage' || field === 'selectedDiscountAmount' || field === 'selectedQuantity' || field === 'selectedRate') {
          const quantity = Math.max(0, parseFloat(updatedItem.selectedQuantity) || 0);
          const rate = Math.max(0, parseFloat(updatedItem.selectedRate) || 0);
          const amount = quantity * rate;

          if (field === 'selectedDiscountPercentage') {
            // When discount % changes, update discount amount
            const discountPct = Math.min(Math.max(0, parseFloat(value) || 0), 100);
            const discountAmount = (amount * discountPct) / 100;
            updatedItem.selectedDiscountPercentage = discountPct;
            updatedItem.selectedDiscountAmount = Math.round(discountAmount * 100) / 100;
          } else if (field === 'selectedDiscountAmount') {
            // When discount amount changes, update discount %
            const discountAmount = Math.min(Math.max(0, parseFloat(value) || 0), amount);
            const discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
            updatedItem.selectedDiscountAmount = Math.round(discountAmount * 100) / 100;
            updatedItem.selectedDiscountPercentage = Math.round(discountPct * 100) / 100;
          } else if (field === 'selectedQuantity' || field === 'selectedRate') {
            // When quantity or rate changes, recalculate discount amount based on current %
            const discountPct = Math.min(Math.max(0, parseFloat(updatedItem.selectedDiscountPercentage) || 0), 100);
            const discountAmount = (amount * discountPct) / 100;
            updatedItem.selectedDiscountAmount = Math.round(discountAmount * 100) / 100;
            
            // If discount amount would exceed new amount, adjust both
            if (discountAmount > amount) {
              updatedItem.selectedDiscountAmount = amount;
              updatedItem.selectedDiscountPercentage = 100;
            }
          }
        }
        
        return updatedItem;
      }
      return item;
    }));
  };

  // Add selected items to return details
  const handleAddSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    setAddingItemsLoading(true);

    try {
      const useIGST = isIGSTInvoice();

      // Check which items are already added
      const existingItemIds = items.map(d => d.itemId);
      const itemsToAdd = selectedItems.filter(
        selectedItem => !existingItemIds.includes(selectedItem.Id)
      );

      if (itemsToAdd.length === 0) {
        handleCloseGetItemsModal();
        return;
      }

      const newReturnItems = itemsToAdd.map(selectedItem => {
        const quantity = parseFloat(selectedItem.selectedQuantity) || 0;
        const rate = parseFloat(selectedItem.selectedRate) || 0;
        const gstRate = parseFloat(selectedItem.GSTRate) || 0;
        const discountPct = Math.min(parseFloat(selectedItem.selectedDiscountPercentage) || 0, 100);
        const discountAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
        const isIncludingTax = formData.gstType === 'Including Tax';

        let taxableAmount, gstAmount, baseAmount, finalDiscountAmount;

        if (isIncludingTax) {
          // Rate includes GST - reverse calculate
          const totalWithGst = quantity * rate;
          baseAmount = totalWithGst / (1 + gstRate / 100);
        } else {
          // Rate excludes GST - forward calculate
          baseAmount = quantity * rate;
        }

        finalDiscountAmount = discountAmt > 0 ? Math.min(discountAmt, baseAmount) : (baseAmount * discountPct) / 100;
        taxableAmount = baseAmount - finalDiscountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;

        return {
          id: Date.now() + Math.random(),
          itemId: selectedItem.Id,
          item: selectedItem.Code ? `${selectedItem.Code} - ${selectedItem.Name}` : (selectedItem.Name || ''),
          hsnCode: selectedItem.HSNCode || '',
          purchasedQty: 0,
          rcvdQty: quantity,
          rate: rate,
          discPercent: discountAmt > 0 ? (finalDiscountAmount / baseAmount) * 100 : discountPct,
          discAmt: finalDiscountAmount,
          gstPercent: gstRate,
          taxable: Math.round(taxableAmount * 100) / 100,
          cgst: useIGST ? 0 : Math.round((gstAmount / 2) * 100) / 100,
          sgst: useIGST ? 0 : Math.round((gstAmount / 2) * 100) / 100,
          igst: useIGST ? Math.round(gstAmount * 100) / 100 : 0,
          total: Math.round((taxableAmount + gstAmount) * 100) / 100
        };
      });

      setItems(prev => [...prev, ...newReturnItems]);
      calculateSummary([...items, ...newReturnItems], additionalCharges);

      handleCloseGetItemsModal();

    } catch (error) {
      console.error('Error adding selected items:', error);
    } finally {
      setAddingItemsLoading(false);
    }
  };

  // Handle search in Get Items modal
  const handleGetItemsSearch = (searchValue) => {
    setGetItemsSearchValue(searchValue);
    setCurrentItemsPage(1);
  };

  // Memoized filtered items for better performance
  const filteredItems = React.useMemo(() => {
    if (!Array.isArray(itemsList) || itemsList.length === 0) {
      return [];
    }

    if (!getItemsSearchValue.trim()) {
      return itemsList;
    }

    const searchTerm = getItemsSearchValue.toLowerCase().trim();
    return itemsList.filter(item =>
      item.Name?.toLowerCase().includes(searchTerm) ||
      item.HSNCode?.toLowerCase().includes(searchTerm)
    );
  }, [itemsList, getItemsSearchValue]);

  // Get paginated items for performance
  const getPaginatedItems = React.useMemo(() => {
    const startIndex = (currentItemsPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, currentItemsPage, itemsPerPage]);

  // Calculate total pages
  const getTotalPages = React.useMemo(() => {
    return Math.ceil(filteredItems.length / itemsPerPage);
  }, [filteredItems.length, itemsPerPage]);

  // Check if item is already added to return (optimized with Set lookup)
  const isItemAlreadyInReturn = React.useCallback((itemId) => {
    return items.some(detail => detail.itemId === itemId);
  }, [items]);

  // Format currency helper
  const formatCurrency = (amt) => `₹${(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Block minus key and 'e' in number inputs to prevent negative values
  const preventNegativeInput = (e) => {
    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  const handleItemChange = (id, field, value) => {
    // Block negative values for all numeric fields
    if (['rcvdQty', 'rate', 'discPercent', 'discAmt'].includes(field)) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue < 0) return;
    }

    // Validate discount percentage not above 100%
    if (field === 'discPercent') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > 100) return;
    }

    // Validate discount amount doesn't exceed maximum allowed (100% of item amount)
    if (field === 'discAmt') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const currentItem = items.find(item => item.id === id);
        if (currentItem) {
          const qty = Math.max(0, parseFloat(currentItem.rcvdQty) || 0);
          const rate = Math.max(0, parseFloat(currentItem.rate) || 0);
          const baseAmount = qty * rate;
          if (numValue > baseAmount) {
            // Block the input if it exceeds 100% of item amount
            return;
          }
        }
      }
    }

    const updatedItems = items.map(item => {
      if (item.id !== id) return item;

      const updated = { ...item, [field]: value };

      // If item is selected from dropdown, populate item details
      if (field === 'itemId' && value) {
        const selectedItem = itemsList.find(i => i.Id === parseInt(value));
        if (selectedItem) {
          console.log('Selected item data:', selectedItem);
          updated.item = selectedItem.Code ? `${selectedItem.Code} - ${selectedItem.Name}` : (selectedItem.Name || '');
          updated.hsnCode = selectedItem.HSNCode || selectedItem.HSN || '';
          updated.gstPercent = selectedItem.GSTRate || selectedItem.GST || 0;
          // Try multiple rate fields
          updated.rate = selectedItem.PurchaseRate || selectedItem.Rate || selectedItem.SaleRate || selectedItem.MRP || 0;
          console.log('Updated rate:', updated.rate);
        }
      }

      // Validate return quantity doesn't exceed purchased quantity
      if (field === 'rcvdQty') {
        const returnQty = parseFloat(value) || 0;
        const purchasedQty = parseFloat(item.purchasedQty) || 0;

        // If purchased quantity exists and return quantity exceeds it, cap it
        if (purchasedQty > 0 && returnQty > purchasedQty) {
          updated.rcvdQty = purchasedQty;
        }
      }

      // Recalculate when quantity, rate, discount, or GST changes
      if (['rcvdQty', 'rate', 'discPercent', 'discAmt', 'gstPercent', 'itemId'].includes(field)) {
        const qty = Math.max(0, parseFloat(updated.rcvdQty) || 0);
        const rate = Math.max(0, parseFloat(updated.rate) || 0);
        const gstRate = Math.max(0, parseFloat(updated.gstPercent) || 0);
        const isIncludingTax = formData.gstType === 'Including Tax';
        const useIGST = isIGSTInvoice();

        let taxableAmount, gstAmount, amount, discountAmount, discountPercent;
        let baseAmount;

        if (isIncludingTax) {
          // Rate includes GST - reverse calculate
          const totalWithGst = qty * rate;
          baseAmount = totalWithGst / (1 + gstRate / 100);
        } else {
          // Rate excludes GST - forward calculate
          baseAmount = qty * rate;
        }

        // Bidirectional discount sync logic
        if (field === 'discAmt') {
          // When discount amount changes, update discount %
          discountAmount = Math.max(0, parseFloat(value) || 0);
          // Ensure discount amount doesn't exceed 100% of base amount
          discountAmount = Math.min(discountAmount, baseAmount);
          discountPercent = baseAmount > 0 ? (discountAmount / baseAmount) * 100 : 0;
          updated.discAmt = Math.round(discountAmount * 100) / 100;
          updated.discPercent = Math.round(discountPercent * 100) / 100;
        } else if (field === 'discPercent') {
          // When discount % changes, update discount amount
          discountPercent = Math.min(Math.max(0, parseFloat(value) || 0), 100);
          discountAmount = (baseAmount * discountPercent) / 100;
          updated.discPercent = discountPercent;
          updated.discAmt = Math.round(discountAmount * 100) / 100;
        } else if (field === 'rcvdQty' || field === 'rate') {
          // When quantity or rate changes, recalculate discount amount based on current %
          discountPercent = Math.min(Math.max(0, parseFloat(updated.discPercent) || 0), 100);
          discountAmount = (baseAmount * discountPercent) / 100;
          updated.discAmt = Math.round(discountAmount * 100) / 100;
          
          // If discount amount would exceed new base amount, adjust both
          if (discountAmount > baseAmount) {
            updated.discAmt = baseAmount;
            updated.discPercent = 100;
          }
        } else {
          // For other fields, use existing discount values
          discountPercent = Math.max(0, parseFloat(updated.discPercent) || 0);
          discountAmount = Math.max(0, parseFloat(updated.discAmt) || 0);
        }

        taxableAmount = baseAmount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;
        amount = taxableAmount + gstAmount;

        // Round values
        updated.taxable = Math.round(taxableAmount * 100) / 100;
        updated.cgst = useIGST ? 0 : Math.round((gstAmount / 2) * 100) / 100;
        updated.sgst = useIGST ? 0 : Math.round((gstAmount / 2) * 100) / 100;
        updated.igst = useIGST ? Math.round(gstAmount * 100) / 100 : 0;
        updated.total = Math.round(amount * 100) / 100;
      }

      return updated;
    });

    setItems(updatedItems);
    calculateSummary(updatedItems, additionalCharges);
  };

  const calculateSummary = (itemsList, charges) => {
    const useIGST = isIGSTInvoice();

    // Find the maximum GST rate from items
    const maxItemGSTRate = itemsList.reduce((max, item) => {
      const gstRate = Math.max(0, parseFloat(item.gstPercent) || 0);
      return Math.max(max, gstRate);
    }, 0);

    // Default freight and packing GST rate is 18%, but use max item GST if it's higher
    const defaultFreightPackingGSTRate = 18;
    const effectiveGSTRate = Math.max(maxItemGSTRate, defaultFreightPackingGSTRate);

    const itemTotals = itemsList.reduce((acc, item) => {
      const qty = Math.max(0, parseFloat(item.rcvdQty) || 0);
      const rate = Math.max(0, parseFloat(item.rate) || 0);
      const discPercent = Math.max(0, parseFloat(item.discPercent) || 0);
      const gstRate = Math.max(0, parseFloat(item.gstPercent) || 0);
      const isIncludingTax = formData.gstType === 'Including Tax';

      let taxableAmount, gstAmount, discountAmount;

      if (isIncludingTax) {
        const totalWithGst = qty * rate;
        const baseAmount = totalWithGst / (1 + gstRate / 100);
        discountAmount = (baseAmount * discPercent) / 100;
        taxableAmount = baseAmount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;
      } else {
        const baseAmount = qty * rate;
        discountAmount = (baseAmount * discPercent) / 100;
        taxableAmount = baseAmount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;
      }

      return {
        subtotal: acc.subtotal + (qty * rate),
        discount: acc.discount + discountAmount,
        taxable: acc.taxable + taxableAmount,
        cgst: acc.cgst + (useIGST ? 0 : gstAmount / 2),
        sgst: acc.sgst + (useIGST ? 0 : gstAmount / 2),
        igst: acc.igst + (useIGST ? gstAmount : 0)
      };
    }, { subtotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0 });

    const freight = parseFloat(charges.freight) || 0;
    const packing = parseFloat(charges.packing) || 0;
    const tcs = parseFloat(charges.tcsAmount) || 0;
    const paid = parseFloat(charges.paidAmount) || 0;

    // Calculate freight and packing GST
    const freightGST = (freight * effectiveGSTRate) / 100;
    const packingGST = (packing * effectiveGSTRate) / 100;
    const totalFreightPackingGST = freightGST + packingGST;

    const grandTotal = itemTotals.taxable + itemTotals.cgst + itemTotals.sgst + itemTotals.igst + freight + packing + totalFreightPackingGST + tcs;
    const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
    const total = Math.round(grandTotal);

    setSummary({
      subtotal: Math.round(itemTotals.subtotal * 100) / 100,
      discount: Math.round(itemTotals.discount * 100) / 100,
      taxable: Math.round(itemTotals.taxable * 100) / 100,
      cgst: Math.round((itemTotals.cgst + (useIGST ? 0 : totalFreightPackingGST / 2)) * 100) / 100,
      sgst: Math.round((itemTotals.sgst + (useIGST ? 0 : totalFreightPackingGST / 2)) * 100) / 100,
      igst: Math.round((itemTotals.igst + (useIGST ? totalFreightPackingGST : 0)) * 100) / 100,
      freightGST: Math.round(freightGST * 100) / 100,
      packingGST: Math.round(packingGST * 100) / 100,
      effectiveGSTRate: effectiveGSTRate,
      tcs: tcs,
      roundOff: Math.round(roundOff * 100) / 100,
      total: total,
      paid: paid
    });
  };

  const handleSubmit = async () => {
    // Clear previous validation errors
    setValidationError('');

    // Validation: Check if there are any items
    if (items.length === 0) {
      setValidationError('Please add at least one item to the return.');
      return;
    }

    // Validation: Check if at least one item has Return Qty > 0
    const itemsWithReturnQty = items.filter(item => (parseFloat(item.rcvdQty) || 0) > 0);

    if (itemsWithReturnQty.length === 0) {
      setValidationError('Please enter Return Qty for at least one item.');
      return;
    }

    // Validation: Check if items with Return Qty have Item selected
    const invalidItems = [];
    itemsWithReturnQty.forEach((item, index) => {
      if (!item.itemId || item.itemId === '') {
        invalidItems.push(index + 1);
      }
    });

    if (invalidItems.length > 0) {
      setValidationError(`Please select an item for row(s): ${invalidItems.join(', ')}`);
      return;
    }

    // Additional validation
    const validationErrors = [];

    // Validate no negative values and return qty <= purchase qty
    const negativeRows = [];
    const excessQtyRows = [];
    items.forEach((item, index) => {
      const rcvdQty = parseFloat(item.rcvdQty) || 0;
      const rate = parseFloat(item.rate) || 0;
      const discPercent = parseFloat(item.discPercent) || 0;
      const discAmt = parseFloat(item.discAmt) || 0;

      if (rcvdQty < 0 || rate < 0 || discPercent < 0 || discAmt < 0) {
        negativeRows.push(index + 1);
      }

      const purchasedQty = parseFloat(item.purchasedQty) || 0;
      if (purchasedQty > 0 && rcvdQty > purchasedQty) {
        excessQtyRows.push(index + 1);
      }
    });

    if (negativeRows.length > 0) {
      setValidationError(`Negative values are not allowed. Please correct row(s): ${negativeRows.join(', ')}`);
      return;
    }

    if (excessQtyRows.length > 0) {
      setValidationError(`Return Qty cannot exceed Purchase Qty in row(s): ${excessQtyRows.join(', ')}`);
      return;
    }

    if (!formData.billNo?.trim()) {
      validationErrors.push('Return Number is required');
    }

    if (!formData.billDate) {
      validationErrors.push('Return Date is required');
    }

    if (validationErrors.length > 0) {
      setValidationError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setLoadingMessage('Creating purchase return...');

    try {
      const gstCalculationType = formData.gstType === 'Including Tax' ? 2 : 1;

      const dataToSend = {
        PurchaseReturn: {
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          PurchaseId: parseInt(formData.purchaseId) || 0,
          PurchaseNo: formData.purchaseNo || '',
          PurchaseDate: formData.purchaseDate || null,
          BillNumber: formData.billNo,
          BillDate: formData.billDate,
          MasterId: parseInt(formData.supplierId),
          MasterName: formData.supplier,
          InvoiceTypeId: 0,
          GSTNumber: formData.gstNo || '',
          GstCalculationType: gstCalculationType,
          GstCalculationTypeName: formData.gstType || 'Excluding Tax',
          Amount: parseFloat(summary.subtotal) || 0,
          DiscountAmount: parseFloat(summary.discount) || 0,
          TaxableAmount: parseFloat(summary.taxable) || 0,
          CGSTAmount: parseFloat(summary.cgst) || 0,
          SGSTAmount: parseFloat(summary.sgst) || 0,
          IGSTAmount: parseFloat(summary.igst) || 0,
          FreightAmount: parseFloat(additionalCharges.freight) || 0,
          FreightGSTRate: parseFloat(summary.effectiveGSTRate) || 0,
          FreightGSTAmount: parseFloat(summary.freightGST) || 0,
          PackingAmount: parseFloat(additionalCharges.packing) || 0,
          PackingGSTRate: parseFloat(summary.effectiveGSTRate) || 0,
          PackingGSTAmount: parseFloat(summary.packingGST) || 0,
          TCSAmount: parseFloat(additionalCharges.tcsAmount) || 0,
          RoundOff: parseFloat(summary.roundOff) || 0,
          TotalAmount: parseFloat(summary.total) || 0,
          PaidAmount: parseFloat(additionalCharges.paidAmount) || 0,
          BalanceAmount: parseFloat(summary.total - additionalCharges.paidAmount) || 0,
          Remarks: additionalCharges.remarks || '',
          IsActive: true,
          CreatedBy: userId
        },
        PurchaseReturnDetails: itemsWithReturnQty.map(item => ({
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          PurchaseReturnId: 0,
          ItemId: parseInt(item.itemId),
          ItemName: item.item || '',
          BatchNumber: item.batchNumber || '',
          PurchaseId: parseInt(formData.purchaseId) || 0,
          PurchaseQuantity: parseFloat(item.purchasedQty) || 0,
          Quantity: parseFloat(item.rcvdQty) || 0,
          ReturnStockQuantity: parseFloat(item.rcvdQty) || 0,
          Rate: parseFloat(item.rate) || 0,
          Amount: parseFloat((item.rcvdQty || 0) * (item.rate || 0)) || 0,
          DiscountPercentage: parseFloat(item.discPercent) || 0,
          DiscountAmount: parseFloat(item.discAmt) || 0,
          HSNCode: item.hsnCode || '',
          GSTRate: parseFloat(item.gstPercent) || 0,
          TaxableAmount: parseFloat(item.taxable) || 0,
          CGSTAmount: parseFloat(item.cgst) || 0,
          SGSTAmount: parseFloat(item.sgst) || 0,
          IGSTAmount: parseFloat(item.igst) || 0,
          TotalAmount: parseFloat(item.total) || 0,
          IsActive: true,
          CreatedBy: userId
        }))
      };

      console.log('Data to send:', dataToSend);

      await postData('PurchaseReturnApi', '', dataToSend, token);

      setSuccess('Purchase return created successfully!');
      // Reset all form state before closing so stale data never shows on next open
      setFormData({
        billNo: '',
        billDate: new Date().toISOString().split('T')[0],
        purchaseId: '',
        purchaseNo: '',
        supplierId: '',
        supplier: '',
        gstNo: '',
        invType: '',
        gstType: 'Excluding Tax'
      });
      setItems([]);
      setAdditionalCharges({ freight: 0, packing: 0, tcsAmount: 0, paidAmount: 0, remarks: '' });
      setSummary({ subtotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, freightGST: 0, packingGST: 0, effectiveGSTRate: 18, tcs: 0, roundOff: 0, total: 0, paid: 0 });
      setValidationError('');
      setSelectedItems([]);
      fetchPurchaseReturns();
      onClose();
    } catch (error) {
      console.error('Error creating purchase return:', error);
      setError('Failed to create purchase return.');
    }

    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '95vh',
          height: '95vh'
        }
      }}
    >
      <DialogTitle sx={{
        bgcolor: '#1e293b',
        borderBottom: '1px solid #334155',
        p: '6px 12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>
          ⭐ Create Purchase Return
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ p: 0.25, color: '#94a3b8', '&:hover': { color: '#fff' } }}>
          <Close sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: '8px 10px', bgcolor: '#f8fafc', overflow: 'auto' }}>
        {validationError && (
          <Alert severity="error" onClose={() => setValidationError('')} sx={{ mb: 1, py: 0, fontSize: '0.75rem' }}>
            {validationError}
          </Alert>
        )}

        {/* Header fields — single compact row */}
        <Box sx={{ mb: 1, p: '6px 10px', bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Purchase Return Information
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', alignItems: 'flex-end' }}>
            {[
              { label: 'Bill No *', field: 'billNo', disabled: true, width: 100 },
              { label: 'Bill Date *', field: 'billDate', type: 'date', width: 120 },
            ].map(({ label, field, disabled, type, width }) => (
              <Box key={field} sx={{ minWidth: width, flexShrink: 0 }}>
                <Typography sx={{ fontSize: '0.65rem', color: '#64748b', mb: 0.25 }}>{label}</Typography>
                <input
                  type={type || 'text'}
                  value={formData[field]}
                  disabled={disabled}
                  onChange={e => !disabled && handleChange(field, e.target.value)}
                  style={{ width: '100%', height: 28, padding: '0 6px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, background: disabled ? '#f1f5f9' : '#fff', boxSizing: 'border-box' }}
                />
              </Box>
            ))}
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: '0.65rem', color: '#64748b', mb: 0.25 }}>Supplier *</Typography>
              <SearchableDropdown
                value={formData.supplierId}
                onChange={(val) => handleChange('supplierId', val)}
                options={suppliers.map(s => ({ value: String(s.MasterId || s.Id), label: s.Name }))}
                placeholder="Select Supplier"
                minWidth={200}
                variant="light"
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography sx={{ fontSize: '0.65rem', color: '#64748b', mb: 0.25 }}>Purchase No (Optional)</Typography>
              <SearchableDropdown
                value={formData.purchaseId}
                onChange={(val) => handleChange('purchaseId', val)}
                options={
                  (formData.supplierId
                    ? purchases.filter(p => String(p.MasterId) === String(formData.supplierId))
                    : purchases
                  ).map(p => ({ value: String(p.Id), label: `${p.BillNumber} - ${p.MasterName}` }))
                }
                placeholder="None"
                minWidth={160}
                variant="light"
              />
            </Box>
            <Box sx={{ minWidth: 120, flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.65rem', color: '#64748b', mb: 0.25 }}>GST No</Typography>
              <input value={formData.gstNo} onChange={e => handleChange('gstNo', e.target.value)}
                style={{ width: '100%', height: 28, padding: '0 6px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, background: '#fff' }} />
            </Box>
            <Box sx={{ minWidth: 80, flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.65rem', color: '#64748b', mb: 0.25 }}>Inv Type</Typography>
              <input value={formData.invType} disabled
                style={{ width: '100%', height: 28, padding: '0 6px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, background: '#f1f5f9' }} />
            </Box>
            <Box sx={{ minWidth: 110, flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.65rem', color: '#64748b', mb: 0.25 }}>GST Type *</Typography>
              <select value={formData.gstType} onChange={e => handleChange('gstType', e.target.value)}
                style={{ width: '100%', height: 28, padding: '0 6px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, background: '#fff' }}>
                <option value="Excluding Tax">Excluding Tax</option>
                <option value="Including Tax">Including Tax</option>
              </select>
            </Box>
          </Box>
        </Box>
        {/* Return Items Section */}
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Return Items {items.length > 0 && `(${items.length})`}
            </Typography>
            {!formData.purchaseId && (
              <Button variant="outlined" size="small" startIcon={<List sx={{ fontSize: 13 }} />} onClick={handleOpenGetItemsModal}
                sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.25, px: 1, borderColor: '#059669', color: '#059669', minHeight: 24, '&:hover': { borderColor: '#047857', bgcolor: '#f0fdf4' } }}>
                Get Items
              </Button>
            )}
          </Box>
          <TableContainer component={Paper} sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', maxHeight: '35vh', overflow: 'auto', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['#','Item','HSN', ...(formData.purchaseId ? ['Purch Qty'] : []), 'Return Qty *','Rate','Amount','Disc%','Disc Amt','GST%','Taxable', isIGSTInvoice() ? 'IGST' : 'GST Total','Total',''].map((h, i) => (
                    <TableCell key={i} sx={{ fontSize: '0.62rem', fontWeight: 700, px: 0.5, py: 0.3, color: '#d97706', bgcolor: '#fef3c7', border: '1px solid #fde68a', whiteSpace: 'nowrap', textAlign: ['Rate','Amount','Disc%','Disc Amt','Taxable','IGST','GST Total','Total'].includes(h) ? 'right' : ['#','HSN','Purch Qty','Return Qty *','GST%'].includes(h) ? 'center' : 'left' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} sx={{ textAlign: 'center', py: 3, color: '#94a3b8', fontSize: '0.75rem' }}>
                      Select a purchase or use Get Items to add return items
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, index) => (
                    <TableRow key={item.id} sx={{ '&:hover': { bgcolor: '#fffbeb' }, height: 28 }}>
                      <TableCell sx={{ px: 0.5, py: 0.15, fontSize: '0.7rem', border: '1px solid #e5e7eb', textAlign: 'center', color: '#94a3b8' }}>{index + 1}</TableCell>
                      <TableCell sx={{ px: 0.5, py: 0.15, border: '1px solid #e5e7eb', minWidth: 140 }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#1e293b' }}>{item.item || '-'}</span>
                      </TableCell>
                      <TableCell sx={{ px: 0.5, py: 0.15, textAlign: 'center', fontSize: '0.7rem', color: '#64748b', border: '1px solid #e5e7eb' }}>{item.hsnCode || '-'}</TableCell>
                      {formData.purchaseId && (
                        <TableCell sx={{ px: 0.5, py: 0.15, textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#1976d2', border: '1px solid #e5e7eb' }}>{item.purchasedQty || 0}</TableCell>
                      )}
                      <TableCell sx={{ px: 0.5, py: 0.15, textAlign: 'center', border: '1px solid #e5e7eb' }}>
                        <TextField size="small" type="number" value={item.rcvdQty}
                          onChange={(e) => handleItemChange(item.id, 'rcvdQty', e.target.value)}
                          onKeyDown={preventNegativeInput}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ min: 0, max: item.purchasedQty || 999999, step: 1, style: { textAlign: 'center', padding: '1px 3px', fontSize: '0.7rem' } }}
                          sx={{ width: 55, '& .MuiOutlinedInput-root': { height: 22 } }} />
                      </TableCell>
                      <TableCell sx={{ px: 0.5, py: 0.15, border: '1px solid #e5e7eb' }}>
                        <TextField size="small" type="number" value={item.rate}
                          onChange={(e) => handleItemChange(item.id, 'rate', e.target.value)}
                          onKeyDown={preventNegativeInput}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', padding: '1px 3px', fontSize: '0.7rem' } }}
                          sx={{ width: 70, '& .MuiOutlinedInput-root': { height: 22 } }} />
                      </TableCell>
                      <TableCell sx={{ px: 0.5, py: 0.15, textAlign: 'right', fontSize: '0.7rem', color: '#64748b', border: '1px solid #e5e7eb' }}>
                        ₹{((item.rcvdQty || 0) * (item.rate || 0)).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ px: 0.5, py: 0.15, border: '1px solid #e5e7eb' }}>
                        <TextField size="small" type="number" value={item.discPercent}
                          onChange={(e) => handleItemChange(item.id, 'discPercent', e.target.value)}
                          onKeyDown={preventNegativeInput}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ min: 0, max: 100, step: 0.01, style: { textAlign: 'right', padding: '1px 3px', fontSize: '0.7rem' } }}
                          sx={{ width: 50, '& .MuiOutlinedInput-root': { height: 22 } }} />
                      </TableCell>
                      <TableCell sx={{ px: 0.5, py: 0.15, border: '1px solid #e5e7eb' }}>
                        <TextField size="small" type="number" value={item.discAmt}
                          onChange={(e) => handleItemChange(item.id, 'discAmt', e.target.value)}
                          onKeyDown={preventNegativeInput}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', padding: '1px 3px', fontSize: '0.7rem' } }}
                          sx={{ width: 60, '& .MuiOutlinedInput-root': { height: 22 } }} />
                      </TableCell>
                      <TableCell sx={{ px: 0.5, py: 0.15, textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#059669', border: '1px solid #e5e7eb' }}>{item.gstPercent}%</TableCell>
                      <TableCell sx={{ px: 0.5, py: 0.15, textAlign: 'right', fontSize: '0.7rem', border: '1px solid #e5e7eb' }}>₹{(item.taxable || 0).toFixed(2)}</TableCell>
                      <TableCell sx={{ px: 0.5, py: 0.15, textAlign: 'right', fontSize: '0.7rem', border: '1px solid #e5e7eb' }}>₹{((item.cgst || 0) + (item.sgst || 0) + (item.igst || 0)).toFixed(2)}</TableCell>
                      <TableCell sx={{ px: 0.5, py: 0.15, textAlign: 'right', fontSize: '0.7rem', fontWeight: 600, border: '1px solid #e5e7eb' }}>₹{(item.total || 0).toFixed(2)}</TableCell>
                      <TableCell sx={{ px: 0.25, py: 0.15, border: '1px solid #e5e7eb' }}>
                        <IconButton size="small" onClick={() => handleDeleteItem(item.id)} color="error" sx={{ p: 0.15 }}>
                          <Delete sx={{ fontSize: 13 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {/* Totals Row */}
                {items.length > 0 && (
                  <TableRow sx={{ bgcolor: '#f0f9ff', borderTop: '2px solid #1976d2' }}>
                    <TableCell colSpan={3} sx={{ px: 0.75, py: 0.5, fontSize: '0.75rem', fontWeight: 700, border: '1px solid #e5e7eb', textAlign: 'right' }}>Total:</TableCell>
                    {formData.purchaseId && (
                      <TableCell sx={{ px: 0.75, py: 0.5, textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                        {items.reduce((s, i) => s + (parseFloat(i.purchasedQty) || 0), 0)}
                      </TableCell>
                    )}
                    <TableCell sx={{ px: 0.75, py: 0.5, textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#059669', border: '1px solid #e5e7eb' }}>
                      {items.reduce((s, i) => s + (parseFloat(i.rcvdQty) || 0), 0)}
                    </TableCell>
                    <TableCell sx={{ px: 0.75, py: 0.5, border: '1px solid #e5e7eb' }} />
                    <TableCell sx={{ px: 0.75, py: 0.5, textAlign: 'right', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      ₹{items.reduce((s, i) => s + ((parseFloat(i.rcvdQty) || 0) * (parseFloat(i.rate) || 0)), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ px: 0.75, py: 0.5, border: '1px solid #e5e7eb' }} />
                    <TableCell sx={{ px: 0.75, py: 0.5, textAlign: 'right', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      ₹{items.reduce((s, i) => s + (parseFloat(i.discAmt) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ px: 0.75, py: 0.5, border: '1px solid #e5e7eb' }} />
                    <TableCell sx={{ px: 0.75, py: 0.5, textAlign: 'right', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      ₹{items.reduce((s, i) => s + (parseFloat(i.taxable) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ px: 0.75, py: 0.5, textAlign: 'right', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      ₹{items.reduce((s, i) => s + ((parseFloat(i.cgst) || 0) + (parseFloat(i.sgst) || 0) + (parseFloat(i.igst) || 0)), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ px: 0.75, py: 0.5, textAlign: 'right', fontSize: '0.75rem', fontWeight: 700, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                      ₹{items.reduce((s, i) => s + (parseFloat(i.total) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ px: 0.75, py: 0.5, border: '1px solid #e5e7eb' }} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Return Summary Section */}
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Return Summary</Typography>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {/* Additional Charges */}
            <Box sx={{ bgcolor: '#fff', p: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', mb: 0.25, textTransform: 'uppercase' }}>Additional Charges</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                {[
                  { label: `Freight (${summary.effectiveGSTRate}%)`, field: 'freight' },
                  { label: `Packing (${summary.effectiveGSTRate}%)`, field: 'packing' },
                  { label: 'TCS Amount', field: 'tcsAmount' },
                  { label: 'Paid Amount', field: 'paidAmount' },
                ].map(({ label, field }) => (
                  <Box key={field}>
                    <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', mb: 0.15, lineHeight: 1 }}>{label}</Typography>
                    <input type="number" value={additionalCharges[field]}
                      onChange={e => handleAdditionalChargeChange(field, e.target.value)}
                      onKeyDown={preventNegativeInput}
                      onFocus={e => e.target.select()}
                      style={{ width: '100%', height: 22, padding: '0 4px', border: '1px solid #cbd5e1', borderRadius: 3, fontSize: 11, textAlign: 'right', boxSizing: 'border-box' }} />
                  </Box>
                ))}
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', mb: 0.15, lineHeight: 1 }}>Remarks</Typography>
                  <input type="text" value={additionalCharges.remarks}
                    onChange={e => handleAdditionalChargeChange('remarks', e.target.value)}
                    placeholder="Return remarks..."
                    style={{ width: '100%', height: 22, padding: '0 4px', border: '1px solid #cbd5e1', borderRadius: 3, fontSize: 11, boxSizing: 'border-box' }} />
                </Box>
              </Box>
            </Box>

            {/* Return Calculation */}
            <Box sx={{ flex: 1, bgcolor: '#fff', p: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 1 }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', mb: 0.25, textTransform: 'uppercase' }}>Return Calculation</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.4, mb: 0.5 }}>
                {[
                  { label: 'Subtotal',    value: summary.subtotal,   color: '#1e293b' },
                  { label: 'Discount',    value: summary.discount,   color: '#dc2626' },
                  { label: 'Taxable',     value: summary.taxable,    color: '#1e293b' },
                  { label: isIGSTInvoice() ? 'IGST' : 'GST Total', value: summary.cgst + summary.sgst + summary.igst, color: '#059669' },
                  { label: 'Freight',     value: additionalCharges.freight || 0, color: '#1e293b' },
                  { label: 'Frt.GST', value: summary.freightGST || 0, color: '#1e293b' },
                  { label: 'Packing',     value: additionalCharges.packing || 0, color: '#1e293b' },
                  { label: 'Pkg.GST', value: summary.packingGST || 0, color: '#1e293b' },
                  { label: 'TCS',         value: summary.tcs,        color: '#1e293b' },
                  { label: 'Round Off',   value: summary.roundOff,   color: '#64748b', prefix: summary.roundOff >= 0 ? '+' : '' },
                ].map(({ label, value, color, prefix = '' }) => (
                  <Box key={label} sx={{ textAlign: 'center', p: '3px 4px', bgcolor: '#f8fafc', borderRadius: 0.5, border: '1px solid #f1f5f9' }}>
                    <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase', lineHeight: 1, mb: 0.15 }}>{label}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color, lineHeight: 1.1 }}>{prefix}₹{(value || 0).toFixed(2)}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                <Box sx={{ bgcolor: '#fff3e0', p: '4px 8px', borderRadius: 1, border: '1.5px solid #ffb74d', textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.58rem', color: '#e65100', fontWeight: 600, lineHeight: 1 }}>Return Total</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#e65100' }}>₹{summary.total.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ bgcolor: '#e3f2fd', p: '4px 8px', borderRadius: 1, border: '1.5px solid #64b5f6', textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.58rem', color: '#1565c0', fontWeight: 600, lineHeight: 1 }}>Balance</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1565c0' }}>₹{(summary.total - summary.paid).toFixed(2)}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: '6px 12px', borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', gap: 1 }}>
        <Button variant="outlined" onClick={onClose}
          sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.5, borderColor: '#94a3b8', color: '#64748b' }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit}
          sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.5, bgcolor: '#1e293b', '&:hover': { bgcolor: '#334155' } }}>
          Save
        </Button>
      </DialogActions>

      {/* View All Items Modal */}
      <Dialog
        open={viewAllItemsModalOpen}
        onClose={() => setViewAllItemsModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          p: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#333' }}>
            All Return Items ({items.length})
          </Typography>
          <IconButton onClick={() => setViewAllItemsModalOpen(false)} size="small" sx={{ p: 0.5 }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2, bgcolor: '#fafafa' }}>
          <TableContainer component={Paper} sx={{ border: '1px solid #ddd', boxShadow: 'none', maxHeight: '500px', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: '#ffe4cc', height: '32px' }}>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', border: '1px solid #ffd4a3', height: '32px' }}>#</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', minWidth: 200, border: '1px solid #ffd4a3', height: '32px' }}>Item</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>HSN</TableCell>
                  {formData.purchaseId && (
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>Purchased Qty</TableCell>
                  )}
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>Return Qty</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Rate</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Amount</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Disc%</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Disc Amt</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>GST%</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Taxable</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>
                    {isIGSTInvoice() ? 'IGST' : 'GST Total'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Total</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', border: '1px solid #ffd4a3', height: '32px' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={item.id} sx={{ '&:hover': { bgcolor: '#f9fafb' }, height: '40px' }}>
                    <TableCell sx={{ p: 0.1, py: 0.3, fontSize: '0.875rem', border: '1px solid #e5e7eb', height: '40px' }}>{index + 1}</TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb', height: '40px' }}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={item.itemId}
                        onChange={(e) => handleItemChange(item.id, 'itemId', e.target.value)}
                        SelectProps={{
                          native: false,
                          MenuProps: {
                            PaperProps: {
                              style: {
                                maxHeight: 300
                              }
                            }
                          }
                        }}
                        sx={{
                          minWidth: 200,
                          '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' }
                        }}
                      >
                        <MenuItem value="">Select Item</MenuItem>
                        {itemsList.map((itm) => (
                          <MenuItem key={itm.Id} value={itm.Id}>
                             {itm.Code ? `${itm.Code} - ${itm.Name}` : itm.Name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.75rem', color: '#666', border: '1px solid #e5e7eb', height: '40px' }}>
                      {item.hsnCode || '-'}
                    </TableCell>
                    {formData.purchaseId && (
                      <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600, color: '#1976d2', border: '1px solid #e5e7eb', height: '40px' }}>
                        {item.purchasedQty || 0}
                      </TableCell>
                    )}
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', border: '1px solid #e5e7eb', height: '40px' }}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.rcvdQty}
                        onChange={(e) => handleItemChange(item.id, 'rcvdQty', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        inputProps={{ min: 0, max: item.purchasedQty || 999999, step: 1, style: { textAlign: 'center' } }}
                        sx={{ width: '80px', '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', border: '1px solid #e5e7eb', height: '40px' }}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.rate}
                        onChange={(e) => handleItemChange(item.id, 'rate', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                        sx={{ width: '90px', '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#666', border: '1px solid #e5e7eb', height: '40px' }}>
                      ₹{((item.rcvdQty || 0) * (item.rate || 0)).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', border: '1px solid #e5e7eb', height: '40px' }}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.discPercent}
                        onChange={(e) => handleItemChange(item.id, 'discPercent', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        inputProps={{ min: 0, max: 100, step: 0.01, style: { textAlign: 'right' } }}
                        sx={{ width: '70px', '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' } }}
                        title="Discount percentage (max 100%)"
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', border: '1px solid #e5e7eb', height: '40px' }}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.discAmt}
                        onChange={(e) => handleItemChange(item.id, 'discAmt', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                        sx={{ width: '80px', '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' } }}
                        title={`Discount amount (max ₹${((item.rcvdQty || 0) * (item.rate || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - 100% of item amount)`}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600, color: '#059669', border: '1px solid #e5e7eb', height: '40px' }}>
                      {item.gstPercent}%
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', border: '1px solid #e5e7eb', height: '40px' }}>
                      ₹{(item.taxable || 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', border: '1px solid #e5e7eb', height: '40px' }}>
                      ₹{((item.cgst || 0) + (item.sgst || 0) + (item.igst || 0)).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 600, border: '1px solid #e5e7eb', height: '40px' }}>
                      ₹{(item.total || 0).toFixed(2)}

                    </TableCell>

                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb', height: '40px' }}>
                      <IconButton size="small" onClick={() => handleDeleteItem(item.id)} color="error" sx={{ p: 0.1 }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                {items.length > 0 && (
                  <TableRow sx={{ bgcolor: '#f0f9ff', borderTop: '2px solid #1976d2' }}>
                    <TableCell colSpan={3} sx={{ p: 0.1, py: 0.3, fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb', textAlign: 'right' }}>
                      Total:
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 700, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                      {items.reduce((sum, item) => sum + (parseFloat(item.purchasedQty) || 0), 0)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 700, color: '#059669', border: '1px solid #e5e7eb' }}>
                      {items.reduce((sum, item) => sum + (parseFloat(item.rcvdQty) || 0), 0)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      ₹{items.reduce((sum, item) => sum + ((parseFloat(item.rcvdQty) || 0) * (parseFloat(item.rate) || 0)), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      ₹{items.reduce((sum, item) => sum + (parseFloat(item.discAmt) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      ₹{items.reduce((sum, item) => sum + (parseFloat(item.taxable) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      ₹{items.reduce((sum, item) => sum + ((parseFloat(item.cgst) || 0) + (parseFloat(item.sgst) || 0) + (parseFloat(item.igst) || 0)), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                      ₹{items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb' }}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 1.5, borderTop: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
          <Button
            variant="outlined"
            onClick={() => setViewAllItemsModalOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              borderColor: '#999',
              color: '#666',
              '&:hover': {
                borderColor: '#666',
                bgcolor: '#f5f5f5'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Get Items Modal */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', m: 2, maxHeight: '95vh' } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <List sx={{ color: '#1e40af', fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1.1rem' }}>
                Get Items ({filteredItems.length} available)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseGetItemsModal} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2, bgcolor: '#fafbfc' }}>
          {getItemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280' }}>Loading items...</Typography>
            </Box>
          ) : (
            <>
              {/* Search */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items by name, HSN code, or item code..."
                  value={getItemsSearchValue}
                  onChange={(e) => handleGetItemsSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: '#6b7280' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiInputBase-input': { py: 1 },
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#fff',
                      '&:hover': { borderColor: '#1e40af' },
                      '&.Mui-focused': { borderColor: '#1e40af' }
                    }
                  }}
                />
              </Box>

              {/* Selected Items Summary */}
              {selectedItems.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mb: 1, display: 'block' }}>
                    Selected Items ({selectedItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedItems.slice(0, 10).map(item => (
                      <Chip
                        key={item.Id}
                        label={`${item.Code ? item.Code + ' - ' : ''}${item.Name} (${item.selectedQuantity})`}
                        size="small"
                        onDelete={() => handleItemSelection(item, false)}
                        sx={{
                          fontSize: '0.75rem',
                          bgcolor: '#fff',
                          '& .MuiChip-deleteIcon': { color: '#1565c0' }
                        }}
                      />
                    ))}
                    {selectedItems.length > 10 && (
                      <Chip
                        label={`+${selectedItems.length - 10} more`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Items Table */}
              <Box sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)', height: '28px' }}>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '4%', fontSize: '0.65rem', lineHeight: '1' }}>
                          <Checkbox
                            size="small"
                            checked={selectedItems.length === getPaginatedItems.length && getPaginatedItems.length > 0}
                            indeterminate={selectedItems.length > 0 && selectedItems.length < getPaginatedItems.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                getPaginatedItems.forEach(item => {
                                  if (!selectedItems.find(si => si.Id === item.Id)) {
                                    handleItemSelection(item, true);
                                  }
                                });
                              } else {
                                getPaginatedItems.forEach(item => {
                                  handleItemSelection(item, false);
                                });
                              }
                            }}
                            sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' }, transform: 'scale(0.8)' }}
                          />
                        </th>
                        <th style={{ padding: '2px 1px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '28%', fontSize: '0.65rem', lineHeight: '1' }}>Item</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem', lineHeight: '1' }}>HSN</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '7%', fontSize: '0.65rem', lineHeight: '1' }}>GST%</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '7%', fontSize: '0.65rem', lineHeight: '1' }}>Qty</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '9%', fontSize: '0.65rem', lineHeight: '1' }}>Rate</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '7%', fontSize: '0.65rem', lineHeight: '1' }}>Disc%</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '9%', fontSize: '0.65rem', lineHeight: '1' }}>Disc Amt</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%', fontSize: '0.65rem', lineHeight: '1' }}>GST Amt</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem', lineHeight: '1' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedItems.map((item, index) => {
                        const selectedItem = selectedItems.find(si => si.Id === item.Id);
                        const isSelected = !!selectedItem;
                        const isAlreadyAdded = isItemAlreadyInReturn(item.Id);

                        return (
                          <tr key={item.Id} style={{
                            borderBottom: '1px solid #f1f5f9',
                            background: isSelected ? '#e3f2fd' : (index % 2 === 0 ? '#fff' : '#f8fafc'),
                            opacity: isAlreadyAdded ? 0.6 : 1,
                            transition: 'background-color 0.2s ease'
                          }}>
                            <td style={{ padding: '4px', textAlign: 'center' }}>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={isAlreadyAdded}
                                onChange={(e) => handleItemSelection(item, e.target.checked)}
                                sx={{
                                  color: isSelected ? '#1e40af' : '#9ca3af',
                                  '&.Mui-checked': { color: '#1e40af' }
                                }}
                              />
                            </td>
                            <td style={{ padding: '4px 6px', fontWeight: 500, color: '#1e293b' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span style={{ fontSize: '0.8rem' }}>{item.Code ? `${item.Code} - ${item.Name}` : item.Name}</span>
                                {isAlreadyAdded && (
                                  <Chip
                                    label="Added"
                                    size="small"
                                    sx={{
                                      bgcolor: '#dcfce7',
                                      color: '#166534',
                                      fontSize: '0.6rem',
                                      height: 16,
                                      '& .MuiChip-label': { px: 0.5 }
                                    }}
                                  />
                                )}
                              </Box>
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center', color: '#6b7280', fontSize: '0.75rem' }}>
                              {item.HSNCode || '-'}
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center', fontWeight: 500, color: '#1e40af', fontSize: '0.75rem' }}>
                              {item.GSTRate || 0}%
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedQuantity}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                  inputProps={{
                                    min: 1,
                                    style: {
                                      textAlign: 'right',
                                      direction: 'ltr'
                                    }
                                  }}
                                  sx={{
                                    width: 60,
                                    '& input': {
                                      textAlign: 'right !important',
                                      direction: 'ltr !important'
                                    },
                                    '& .MuiInputBase-input': {
                                      py: 0.25,
                                      px: 0.5,
                                      fontSize: '0.75rem',
                                      bgcolor: '#fff',
                                      textAlign: 'right !important'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      '&:hover': { borderColor: '#1e40af' },
                                      '&.Mui-focused': { borderColor: '#1e40af' }
                                    }
                                  }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.75rem' }}>1</span>
                              )}
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedRate}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                  inputProps={{
                                    min: 0,
                                    style: {
                                      textAlign: 'right',
                                      direction: 'ltr'
                                    }
                                  }}
                                  sx={{
                                    width: 80,
                                    '& input': {
                                      textAlign: 'right !important',
                                      direction: 'ltr !important'
                                    },
                                    '& .MuiInputBase-input': {
                                      py: 0.25,
                                      px: 0.5,
                                      fontSize: '0.75rem',
                                      bgcolor: '#fff',
                                      textAlign: 'right !important'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      '&:hover': { borderColor: '#1e40af' },
                                      '&.Mui-focused': { borderColor: '#1e40af' }
                                    }
                                  }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#6b7280', fontSize: '0.75rem' }}>
                                  {formatCurrency(item.PurchaseRate || item.SaleRate || 0)}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedDiscountPercentage}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountPercentage', e.target.value)}
                                  inputProps={{
                                    min: 0,
                                    max: 100,
                                    step: 0.01,
                                    style: {
                                      textAlign: 'right',
                                      direction: 'ltr'
                                    }
                                  }}
                                  sx={{
                                    width: 60,
                                    '& input': {
                                      textAlign: 'right !important',
                                      direction: 'ltr !important'
                                    },
                                    '& .MuiInputBase-input': {
                                      py: 0.25,
                                      px: 0.5,
                                      fontSize: '0.75rem',
                                      bgcolor: '#fff',
                                      textAlign: 'right !important'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      '&:hover': { borderColor: '#1e40af' },
                                      '&.Mui-focused': { borderColor: '#1e40af' }
                                    }
                                  }}
                                  title="Discount percentage (max 100%)"
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.75rem' }}>0</span>
                              )}
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedDiscountAmount}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountAmount', e.target.value)}
                                  inputProps={{
                                    min: 0,
                                    max: (parseFloat(selectedItem.selectedQuantity) || 0) * (parseFloat(selectedItem.selectedRate) || 0),
                                    step: 0.01,
                                    style: {
                                      textAlign: 'right',
                                      direction: 'ltr'
                                    }
                                  }}
                                  sx={{
                                    width: 80,
                                    '& input': {
                                      textAlign: 'right !important',
                                      direction: 'ltr !important'
                                    },
                                    '& .MuiInputBase-input': {
                                      py: 0.25,
                                      px: 0.5,
                                      fontSize: '0.75rem',
                                      bgcolor: '#fff',
                                      textAlign: 'right !important'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      '&:hover': { borderColor: '#1e40af' },
                                      '&.Mui-focused': { borderColor: '#1e40af' }
                                    }
                                  }}
                                  title={`Discount amount (max ₹${((parseFloat(selectedItem.selectedQuantity) || 0) * (parseFloat(selectedItem.selectedRate) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - 100% of item amount)`}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.75rem' }}>0</span>
                              )}
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center', fontWeight: 600, color: '#1B5E20', fontSize: '0.75rem' }}>
                              {isSelected ? (
                                (() => {
                                  const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                  const rate = parseFloat(selectedItem.selectedRate) || 0;
                                  const discPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
                                  const discAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
                                  const gstRate = parseFloat(item.GSTRate) || 0;
                                  const amount = qty * rate;
                                  const finalDiscountAmount = discAmt > 0 ? Math.min(discAmt, amount) : (amount * discPct) / 100;
                                  const taxableAmount = amount - finalDiscountAmount;
                                  const gstAmount = (taxableAmount * gstRate) / 100;
                                  return formatCurrency(gstAmount);
                                })()
                              ) : (
                                formatCurrency(((item.PurchaseRate || item.SaleRate || 0) * 1 * (parseFloat(item.GSTRate) || 0)) / 100)
                              )}
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center', fontWeight: 600, color: '#1e40af', fontSize: '0.75rem' }}>
                              {isSelected ? (
                                (() => {
                                  const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                  const rate = parseFloat(selectedItem.selectedRate) || 0;
                                  const discPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
                                  const discAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
                                  const gstRate = parseFloat(item.GSTRate) || 0;
                                  const amount = qty * rate;
                                  const finalDiscountAmount = discAmt > 0 ? Math.min(discAmt, amount) : (amount * discPct) / 100;
                                  const taxableAmount = amount - finalDiscountAmount;
                                  const gstAmount = (taxableAmount * gstRate) / 100;
                                  const total = taxableAmount + gstAmount;
                                  return formatCurrency(total);
                                })()
                              ) : (
                                formatCurrency((item.PurchaseRate || item.SaleRate || 0) * 1)
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Pagination */}
              {getTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={getTotalPages}
                    page={currentItemsPage}
                    onChange={(e, page) => setCurrentItemsPage(page)}
                    size="small"
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button onClick={handleCloseGetItemsModal} variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleAddSelectedItems}
            variant="contained"
            disabled={selectedItems.length === 0 || addingItemsLoading}
            size="small"
            sx={{ bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' } }}
          >
            {addingItemsLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                Adding...
              </>
            ) : (
              `Add Selected Items (${selectedItems.length})`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PurchaseReturnModal;

